/**
 * useEntityListener — Subscribe a React component to entity change events.
 * Auto-cleans up on unmount. Each view registers what it cares about.
 *
 * Usage:
 *   useEntityListener('entity-created', 'Competitor', () => refetch())
 *   useEntityListener('entity-updated', 'Competitor', () => refetch())
 *   useEntityListener('entity-deleted', '*', () => queryClient.invalidateQueries())
 */

import { useEffect, useCallback } from 'react'
import { entityEvents } from '@/lib/entityEventBus'
import type { EntityEventType, EntityEventDetail } from '@/lib/entityEventBus'

export function useEntityListener(
  event: EntityEventType,
  entityType: string,
  handler: (detail: EntityEventDetail) => void,
) {
  const stableHandler = useCallback(handler, [handler])
  useEffect(() => {
    return entityEvents.on(event, entityType, stableHandler)
  }, [event, entityType, stableHandler])
}

/**
 * Convenience: subscribe to all CRUD events for an entity type.
 */
export function useEntityCrudListener(
  entityType: string,
  handler: (detail: EntityEventDetail) => void,
) {
  useEntityListener('entity-created', entityType, handler)
  useEntityListener('entity-updated', entityType, handler)
  useEntityListener('entity-deleted', entityType, handler)
}
